<template>
  <div ref="timelineViewport" class="rounded border border-slate-600 bg-slate-950/50 overflow-x-auto" :class="[fillViewport ? 'w-full min-w-0' : '']" @scroll="updateViewport">
    <div class="relative min-w-[720px]" :style="{ width: `${timelineWidth}px`, minWidth: fillViewport ? '100%' : undefined }">
      <div
        v-for="lane in lanes"
        :key="lane.trackIndex"
        class="grid border-b border-slate-700/70 last:border-b-0 transition-opacity"
        :class="[
          showTrackLabels ? 'grid-cols-[156px_minmax(0,1fr)]' : 'grid-cols-1',
          lane.visual.isActiveTrack && activeTrackIndex != null ? 'border-l-2' : '',
        ]"
        :style="{
          opacity: lane.visual.opacity,
          backgroundColor: lane.visual.isActiveTrack && activeTrackIndex != null ? lane.color.soft : undefined,
          borderLeftColor: lane.visual.isActiveTrack && activeTrackIndex != null ? lane.color.solid : undefined,
        }"
        @click="selectLaneTrack(lane)"
      >
        <div v-if="showTrackLabels" class="sticky left-0 z-30 flex min-w-0 flex-col gap-2 border-r border-slate-600 bg-slate-800 px-2 py-2 text-xs text-slate-200" :title="lane.track.name">
          <div class="flex items-start gap-2">
          <button
            v-if="editable"
            type="button"
            class="mt-0.5 inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded border text-[11px] leading-none transition-colors"
            :class="isPlaybackEnabled(lane.trackIndex)
              ? 'border-transparent bg-current text-slate-950 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]'
              : 'border-slate-500 bg-slate-950/45 text-transparent hover:border-slate-400 hover:bg-slate-900/75'"
            :style="isPlaybackEnabled(lane.trackIndex) ? { color: lane.color.solid, backgroundColor: lane.color.solid } : undefined"
            :aria-pressed="isPlaybackEnabled(lane.trackIndex)"
            :aria-label="`${isPlaybackEnabled(lane.trackIndex) ? 'Disable' : 'Enable'} playback for track ${lane.trackIndex + 1}`"
            title="Include track in playback"
            @click.stop="toggleTrackPlayback(lane.trackIndex)"
          >
            <span v-if="isPlaybackEnabled(lane.trackIndex)" aria-hidden="true" class="font-black text-[12px] leading-none text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.75)]">✓</span>
          </button>
          <span v-else class="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full" :style="{ backgroundColor: lane.color.solid }"></span>
          <div class="min-w-0">
            <div class="truncate font-semibold">T{{ lane.trackIndex + 1 }}</div>
            <div v-if="showTrackNames" class="truncate text-[11px] leading-4 text-slate-400">{{ lane.track.name || 'Unnamed' }}</div>
          </div>
          </div>
          <label v-if="editable" class="flex w-full min-w-0 items-center gap-3 pl-[2px] text-[10px] uppercase tracking-wide text-slate-500" @click.stop>
            <input
              class="volume-slider block h-1 min-w-0 flex-1 appearance-none"
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="lane.track.volume"
              :style="{
                '--track-color': lane.color.solid,
                '--track-fill': `${Math.round((Number(lane.track.volume) || 0) * 100)}%`,
              }"
              @pointerdown.stop
              @mousedown.stop
              @click.stop
              @input.stop="updateTrackVolume(lane.trackIndex, $event.target.value)"
            />
            <div class="w-9 shrink-0 text-right tabular-nums normal-case text-[10px] leading-none text-slate-400">{{ Math.round((lane.track.volume ?? 0) * 100) }}%</div>
          </label>
        </div>
        <div class="relative h-20 overflow-hidden" @click="placeComposerCursor(lane, $event)">
          <div
            ref="playheadCursor"
            class="absolute inset-y-0 z-20 border-l-2 border-cyan-200 pointer-events-none"
            :style="{ left: `${playheadPositionX}px` }"
          ></div>
          <div
            v-if="showComposerCursor && lane.visual.isActiveTrack"
            class="absolute inset-y-0 z-20 border-l-2 border-dashed border-amber-300 pointer-events-none"
            data-testid="composer-cursor"
            :style="{ left: `${composerCursorX}px` }"
          ></div>
          <template v-for="event in lane.visibleEvents" :key="`${lane.trackIndex}-${event.eventIndex}`">
            <button
              type="button"
              class="absolute h-7 rounded border px-2 py-1 text-left text-xs overflow-hidden"
              :class="eventClass(lane, event)"
              :style="eventStyle(lane, event)"
              :disabled="playing || !lane.visual.isEditable"
              @pointerdown.stop="auditionLaneEvent(lane, event)"
              @click.stop="selectLaneEvent(lane, event)"
            >
              <div class="font-semibold truncate">{{ midiToNoteName(event.note) }}</div>
              <div v-if="eventWidth(event) >= 48" class="truncate opacity-75">{{ ticksToNoteValue(event.durationTicks) }}</div>
            </button>
          </template>
        </div>
      </div>
      <div v-if="lanes.length === 0" class="p-6 text-center text-xs text-slate-500">No enabled tracks</div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { midiToNoteName } from '../../stores/synthModel.js'
import { buildAbsoluteSequenceTiming, filterTimelineRange, followScrollLeft, sequenceEventWidth, sequencePlaybackX, sequenceXToTimeMs } from './synthSequenceGeometry.js'
import { msToTicks, ticksToMs, ticksToNoteValue } from '../../stores/synthTiming.js'
import { getSynthTrackColor, getSynthTrackVisualState } from './synthTrackColors.js'

const props = defineProps({
  tracks: { type: Array, required: true },
  trackIndices: { type: Array, default: () => [] },
  trackPlaybackEnabledIndices: { type: Array, default: () => [] },
  playbackTimeMs: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
  editable: { type: Boolean, default: false },
  fillViewport: { type: Boolean, default: false },
  showTrackLabels: { type: Boolean, default: false },
  showTrackNames: { type: Boolean, default: false },
  showRests: { type: Boolean, default: false },
  selectedEventIndex: { type: Number, default: -1 },
  bpm: { type: Number, default: 120 },
  activeTrackIndex: { type: Number, default: null },
  composeMode: { type: Boolean, default: false },
  cursorTick: { type: Number, default: 0 },
})

const emit = defineEmits(['select-event', 'audition-event', 'select-track', 'toggle-track-playback', 'update-track', 'viewport-change', 'cursor-tick'])

const pixelsPerMs = 0.3
// Matches the sticky label column in the lane grid template below.
const TRACK_LABEL_COLUMN_WIDTH = 156
const CURSOR_FOLLOW_RIGHT_MARGIN = 220
const timelineViewport = ref(null)
const playheadCursor = ref(null)
const viewportWidth = ref(720)
const viewportScrollLeft = ref(0)
const playheadViewportX = ref(120)
let resizeObserver = null
const baseLanes = computed(() => props.tracks.map((track, localIndex) => ({
  track,
  trackIndex: props.trackIndices[localIndex] ?? localIndex,
  color: getSynthTrackColor(props.trackIndices[localIndex] ?? localIndex),
  visual: getSynthTrackVisualState(props.trackIndices[localIndex] ?? localIndex, props.activeTrackIndex, props.editable),
  events: buildAbsoluteSequenceTiming(track?.sequence, props.bpm),
})))
const totalDurationMs = computed(() => Math.max(0, ...baseLanes.value.flatMap(lane => lane.events.map(event => event.endMs))))
const playheadPositionX = computed(() => viewportScrollLeft.value + playheadViewportX.value)
const timelineWidth = computed(() => Math.max(720, playheadViewportX.value + totalDurationMs.value * pixelsPerMs + 160))
const visibleRange = computed(() => ({
  start: (viewportScrollLeft.value - playheadViewportX.value) / pixelsPerMs,
  end: (viewportScrollLeft.value + viewportWidth.value - playheadViewportX.value) / pixelsPerMs,
}))
const lanes = computed(() => baseLanes.value.map(lane => ({
  ...lane,
  visibleEvents: filterTimelineRange(lane.events, visibleRange.value.start, visibleRange.value.end, 1000),
})))
const activeEventIndices = computed(() => new Map(baseLanes.value.map(lane => [
  lane.trackIndex,
  new Set(props.playing
    ? filterTimelineRange(lane.events, props.playbackTimeMs, props.playbackTimeMs + 0.001).map(event => event.eventIndex)
    : []),
])))

const showComposerCursor = computed(() => props.composeMode && props.activeTrackIndex != null)
const composerCursorX = computed(() => sequencePlaybackX(ticksToMs(props.cursorTick, props.bpm), 0, pixelsPerMs, playheadViewportX.value))

function eventStyle(lane, event) {
  return {
    left: `${sequencePlaybackX(event.startMs, 0, pixelsPerMs, playheadViewportX.value)}px`,
    top: `${4 + (event.note % 2) * 34}px`,
    width: `${eventWidth(event)}px`,
    backgroundColor: lane.color.solid,
    borderColor: lane.color.text,
  }
}

function eventWidth(event) {
  return sequenceEventWidth(event.durationMs, pixelsPerMs)
}

function eventClass(lane, event) {
  if (props.playing && lane.visual.isActiveTrack && activeEventIndices.value.get(lane.trackIndex)?.has(event.eventIndex)) return 'ring-2 ring-white brightness-125 text-slate-950'
  if (lane.visual.isEditable && props.selectedEventIndex === event.eventIndex) return 'ring-2 ring-white text-slate-950'
  return 'text-slate-950'
}

function selectLaneEvent(lane, event) {
  if (props.playing || !lane.visual.isEditable) return
  emit('select-event', { trackIndex: lane.trackIndex, eventIndex: event.eventIndex })
}

// Empty lane space: the click places the composer cursor instead of selecting.
function placeComposerCursor(lane, pointerEvent) {
  if (!props.composeMode || !lane.visual.isEditable) return
  const bounds = pointerEvent?.currentTarget?.getBoundingClientRect?.()
  if (!bounds) return
  // The lane content box is the same coordinate space the events are laid out in,
  // so its bounding rect already accounts for horizontal scroll and resize.
  const laneX = Number(pointerEvent.clientX) - bounds.left
  const timeMs = sequenceXToTimeMs(laneX, 0, pixelsPerMs, playheadViewportX.value)
  emit('cursor-tick', { trackIndex: lane.trackIndex, tick: msToTicks(timeMs, props.bpm) })
}

function selectLaneTrack(lane) {
  if (!props.editable) return
  emit('select-track', lane.trackIndex)
}

function isPlaybackEnabled(trackIndex) {
  return props.trackPlaybackEnabledIndices.includes(trackIndex)
}

function toggleTrackPlayback(trackIndex) {
  emit('toggle-track-playback', trackIndex)
}

function updateTrackVolume(trackIndex, value) {
  const volume = Math.max(0, Math.min(1, Number(value) || 0))
  emit('update-track', { index: trackIndex, patch: { volume } })
}

function auditionLaneEvent(lane, event) {
  if (props.playing || !lane.visual.isEditable || event.note == null) return
  emit('audition-event', { trackIndex: lane.trackIndex, eventIndex: event.eventIndex })
}

function updateViewport() {
  viewportScrollLeft.value = timelineViewport.value?.scrollLeft ?? 0
  emitViewport()
}

function setHorizontalScroll(scrollLeft) {
  if (!timelineViewport.value) return
  const maximum = Math.max(0, timelineViewport.value.scrollWidth - timelineViewport.value.clientWidth)
  timelineViewport.value.scrollLeft = Math.max(0, Math.min(maximum, Number(scrollLeft) || 0))
  updateViewport()
}

// Time 0 sits at playheadViewportX in content space, so the playhead keeps its lead-in
// margin at the start of the song instead of the scroll clamping at 0 and parking the
// cursor ahead of tick 0.
function focusTime(timeMs) {
  if (!timelineViewport.value) return
  setHorizontalScroll(Math.max(0, Number(timeMs) || 0) * pixelsPerMs)
}

// Cursor follow reuses the timeline scroll container and the event x geometry; it never
// touches playback state.
function ensureTickVisible(tick) {
  // Wait for the pending render so the content width already covers the new event.
  nextTick(() => followTick(tick))
}

function followTick(tick) {
  const viewport = timelineViewport.value
  if (!viewport) return
  const labelInset = props.showTrackLabels ? TRACK_LABEL_COLUMN_WIDTH : 0
  const cursorX = labelInset + sequencePlaybackX(ticksToMs(tick, props.bpm), 0, pixelsPerMs, playheadViewportX.value)
  const nextScrollLeft = followScrollLeft(cursorX, {
    scrollLeft: viewport.scrollLeft,
    viewportWidth: viewport.clientWidth,
    leftInset: labelInset,
    rightMargin: CURSOR_FOLLOW_RIGHT_MARGIN,
  })
  if (nextScrollLeft == null) return
  setHorizontalScroll(nextScrollLeft)
}

function updatePlayheadViewportX() {
  const viewport = timelineViewport.value
  const cursor = playheadCursor.value
  if (!viewport || !cursor?.getBoundingClientRect) return
  const viewportRect = viewport.getBoundingClientRect()
  const cursorRect = cursor.getBoundingClientRect()
  playheadViewportX.value = Math.max(0, cursorRect.left - viewportRect.left)
}

function emitViewport() {
  const viewport = timelineViewport.value
  if (!viewport) return
  emit('viewport-change', {
    scrollLeft: viewport.scrollLeft,
    viewportWidth: viewport.clientWidth,
    contentWidth: viewport.scrollWidth,
  })
}

onMounted(() => {
  resizeObserver = new ResizeObserver(entries => {
    viewportWidth.value = Math.max(1, entries[0]?.contentRect.width ?? 720)
    updatePlayheadViewportX()
    emitViewport()
  })
  if (timelineViewport.value) resizeObserver.observe(timelineViewport.value)
  updatePlayheadViewportX()
  emitViewport()
})

watch(timelineWidth, () => nextTick(() => {
  updatePlayheadViewportX()
  emitViewport()
}))
watch(viewportScrollLeft, () => nextTick(updatePlayheadViewportX))

onBeforeUnmount(() => resizeObserver?.disconnect())

defineExpose({ focusTime, setHorizontalScroll, ensureTickVisible })
</script>

<style scoped>
.volume-slider {
  background:
    linear-gradient(to right,
      var(--track-color) 0,
      var(--track-color) var(--track-fill),
      rgba(148, 163, 184, 0.28) var(--track-fill),
      rgba(148, 163, 184, 0.28) 100%);
  border-radius: 9999px;
  cursor: pointer;
}

.volume-slider::-webkit-slider-runnable-track {
  height: 2px;
  border-radius: 9999px;
  background: transparent;
}

.volume-slider::-moz-range-track {
  height: 2px;
  border-radius: 9999px;
  background: transparent;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  margin-top: -5px;
  border-radius: 9999px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  background: rgba(244, 244, 245, 0.98);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.45);
}

.volume-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  background: rgba(244, 244, 245, 0.98);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.45);
}

.volume-slider:focus-visible {
  outline: 1px solid rgba(103, 232, 249, 0.85);
  outline-offset: 2px;
}
</style>
